import React, { useState } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { Edit3, Trash2, Check, X } from 'lucide-react';
import { NoteItem as NoteItemType } from '../types/notes';

interface NoteItemProps {
    note: NoteItemType;
    onEdit: (newText: string) => Promise<void>;
    onDelete: () => void;
    submitting?: boolean;
}

export const NoteItem: React.FC<NoteItemProps> = ({
    note,
    onEdit,
    onDelete,
    submitting = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(note.text);
    const [localSubmitting, setLocalSubmitting] = useState(false);

    const handleSaveEdit = async () => {
        if (!editText.trim()) return;

        setLocalSubmitting(true);
        try {
            await onEdit(editText);
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving edit:', err);
        } finally {
            setLocalSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditText(note.text);
        setIsEditing(false);
    };

    const formatDate = (dateStr: string) => {
        // Format: YYYY-MM-DD hh:mm:ss -> DD/MM/YYYY HH:mm
        const [datePart, timePart] = dateStr.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':');
        return `${day}/${month}/${year} ${hour}:${minute}`;
    };

    return (
        <Card className="note-card dropshadow dropshadow-hover">
            <Card.Body className="note-card-body">
                {isEditing ? (
                    <div className="note-edit-mode">
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    handleCancelEdit();
                                }
                            }}
                            autoFocus
                            disabled={localSubmitting || submitting}
                        />
                        <div className="note-edit-actions">
                            <Button
                                size="sm"
                                variant="success"
                                onClick={handleSaveEdit}
                                disabled={localSubmitting || submitting || !editText.trim()}
                            >
                                <Check size={16} />
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleCancelEdit}
                                disabled={localSubmitting || submitting}
                            >
                                <X size={16} />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="note-card-content">
                        <div className="note-content">
                            <div className="note-text">{note.text}</div>
                            <div className="note-metadata">
                                <span className="note-meta-item">
                                    {formatDate(note.createdAt)}
                                </span>
                                {note.updatedAt && (
                                    <>
                                        <span className="note-meta-separator">•</span>
                                        <span className="note-meta-item">
                                            Redigerad {formatDate(note.updatedAt)}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="note-actions">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit3 size={16} />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={onDelete}
                            >
                                <Trash2 size={16} />
                            </Button>
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};
